import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Popconfirm, Space, Table, Tooltip, message } from "antd";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getTitle } from "utils";
import { formatDate } from "utils/date";
import { $url } from "utils/url";
import { ReviewModal } from "./components/ReviewModal";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useDictionary } from "hooks/useDictionary";
import { Dictionary, DictionaryType } from "types/dictionary";
import { dictionaryApi } from "api/dictionary.api";

const { Column } = Table;

export const ReviewPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const { dictionaries, fetchData, loading, query, total } = useDictionary({
    initQuery: { page: 1, limit: 10, type: DictionaryType.ReviewSample },
  });

  const [loadingDelete, setLoadingDelete] = useState(false);

  const modalRef = useRef<ReviewModal>();

  const debounceSearch = useCallback(
    debounce((keyword: string) => {
      query.search = keyword;
      query.page = 1;
      fetchData();
    }, 300),
    [query, fetchData],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id: number) => {
    setLoadingDelete(true);
    try {
      await dictionaryApi.delete(id);
      message.success("Xóa thành công");
      fetchData();
    } finally {
      setLoadingDelete(false);
    }
  };

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm kiếm",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          query.search = "";
                          fetchData();
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  onClick={() => {
                    modalRef.current?.handleCreate();
                  }}
                  tone="primary"
                  icon={<PlusOutlined />}
                >
                  Tạo mới đánh giá
                </AppButton>
              }
              filterLabel={null}
            />
          }
          table={
            <AppTable<Dictionary>
              loading={loading}
              pagination={false}
              rowKey="id"
              dataSource={dictionaries}
            >
          <Column
            title="Ảnh đại diện"
            dataIndex="avatar"
            key="avatar"
            render={(avatar, record: Dictionary) => {
              return (
                <div className="flex gap-2 items-center">
                  <img
                    src={$url(avatar)}
                    width={50}
                    style={{ objectFit: "contain", borderRadius: 5 }}
                    alt=""
                  />
                  <div>{record.name}</div>
                </div>
              );
            }}
          />

          <Column
            title="Nội dung đánh giá"
            dataIndex="description"
            key="description"
          />

          <Column
            title="Ngày tạo"
            dataIndex="createdAt"
            key="phone"
            render={(text) => <span>{formatDate(text)}</span>}
          />

          <Column
            title="Thao tác"
            key="action"
            width={100}
            align="center"
            render={(text, record: Dictionary) => (
              <div className="flex gap-2 justify-center items-center">
                <Tooltip title="Cập nhật">
                  <ActionButton
                    aria-label="Cập nhật"
                    onClick={() => {
                      modalRef.current?.handleUpdate(record);
                    }}
                  >
                    <EditOutlined style={{ color: "#6c84e5" }} />
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Xóa">
                  <Popconfirm
                    placement="top"
                    title={"Xác nhận xóa đánh giá này?"}
                    onConfirm={() => handleDelete(record.id)}
                    okText="Xóa"
                    cancelText="Không"
                  >
                    <ActionButton aria-label="Xóa" loading={loadingDelete}>
                      <DeleteOutlined style={{ color: "#ef4444" }} />
                    </ActionButton>
                  </Popconfirm>
                </Tooltip>
              </div>
            )}
          />
            </AppTable>
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                query.page = page;
                query.limit = limit;
                fetchData();
              }}
            />
          }
        />

      <ReviewModal onSubmit={fetchData} onClose={() => {}} ref={modalRef} />
    </div>
  );
});
